import React from 'react';
import type { PatientDetailsPatientProp } from './internal';
import { PatientDetailsSection } from './internal';
import { intl, intlCountry } from '../../lib';
import { VBox } from '../layout';
import { isEmptyString } from '../../util';
import type { infrastructurePortalUtil } from 'generated-ts-common';

/**
 * Creates a readable string from the given patient Address. When the passed address is
 * null or undefined, an empty string will be returned.
 *
 * @param address the address to format
 * @return the full formatted address of the patient. An empty string if the passed address is null, undefined or contains no valid information.
 */
export const getFullPatientAddress = (
  address:
    | infrastructurePortalUtil.saf.infrastructure.model.patient.Address
    | undefined
    | null
): string => {
  if (!address) {
    return '';
  }

  let addressString = '';
  if (!isEmptyString(address.streetName))
    addressString += address.streetName + ', ';
  if (!isEmptyString(address.dwellingNumber))
    addressString += address.dwellingNumber + ', ';
  if (!isEmptyString(address.postalCode))
    addressString += address.postalCode + ' ';
  if (!isEmptyString(address.city)) addressString += address.city + ', ';
  if (!isEmptyString(address.country))
    addressString += intlCountry(address.country);

  // trim and remove trailing comma
  addressString = addressString.trim();
  if (addressString.charAt(addressString.length - 1) === ',')
    addressString = addressString.substring(0, addressString.length - 1);

  return addressString;
};

/**
 * Props for a {@link PatientDetailsAddress} component.
 */
export type PatientDetailsAddressProps = PatientDetailsPatientProp;

/**
 * Component for displaying the address details of a patient.
 *
 * @param patient the patient whose data is being displayed
 * @param props leftover {@link PatientDetailsAddressProps}
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function PatientDetailsAddress({
  patient,
  ...props
}: Readonly<PatientDetailsAddressProps>): React.JSX.Element {
  const legalAddress = patient.addresses?.find(
    addr => addr.addressType === 'L'
  );

  const businessAddress = patient.addresses?.find(
    addr => addr.addressType === 'B'
  );

  if (!legalAddress && !businessAddress) {
    return <></>;
  }

  return (
    <PatientDetailsSection {...props} className="ehs-patient-details-address">
      {legalAddress && (
        <VBox>
          <h3 className="legal-address-label">
            {intl('view.patient.detail.legalAddress')}
          </h3>
          <span className="legal-address-text">
            {getFullPatientAddress(legalAddress)}
          </span>
        </VBox>
      )}
      {businessAddress && (
        <VBox>
          <h3 className="business-address-label">
            {intl('view.patient.detail.businessAddress')}
          </h3>
          <span className="business-address-text">
            {getFullPatientAddress(businessAddress)}
          </span>
        </VBox>
      )}
    </PatientDetailsSection>
  );
}
